import { put, call, all, takeLatest, delay, cancelled, take, fork, cancel } from 'redux-saga/effects';
import { notification } from 'antd';
import pipefitterApi, { deletePipeData, generateReportPipeData } from '../../services/pipefitterApi';
import {
  submitCompatibilityReport,
  getCompatibilityReport,
  extractSubmitErrorMessage,
} from '../../features/pipefitter/api/compatibilityReportsApi';
import { extractPipeDataFromReportHistory } from '../../features/pipefitter/api/reportHistory';
import {
  buildPipeStatusListQueryParams,
  getPipeStatusListUrl,
  isGetPipeStatusListSuccess,
  parsePipeStatusListResponse,
} from '../../features/pipefitter/api/pipeStatusList';
// TEMPORARY: Demo mode for UI demonstration - remove when real API is ready
import {
  DEMO_SPREAD_CONFIGURATION,
  DEMO_PIPE_COUNTS,
  DEMO_SEQUENCE_LIST,
  DEMO_PIPE_SCAN_DATA,
  DEMO_PIPE_STATISTICS,
  DEMO_PIPE_STATUS_DATA,
} from '../../features/pipefitter/constants/demoData';
import {
  pipefitterStartProjectLoader,
  pipefitterStopProjectLoader,
  pipefitterSetProjectList,
  pipefitterSetPipeCount,
  pipefitterSetTotalPipeCount,
  pipefitterSetUsedPipeCount,
  pipefitterSetSpreadPipeCount,
  pipefitterSetSubSpreadPipeCount,
  pipefitterSetConfigurationLoading,
  pipefitterSetConfigurationList,
  pipefitterSetReportsLoading,
  pipefitterSetInvalidFiles,
  pipefitterSetPipeStatistics,
  pipefitterSetPipeStatus,
  pipefitterSetPipeStatusLoadingMore,
  pipefitterSetProjectSummaryData,
  pipefitterSetPipesListFiltered,
  pipefitterSetSequenceList,
  pipefitterSetSequenceLoadingMore,
  pipefitterSetSequenceLoading,
  pipefitterSetPipeDataGraph,
  pipefitterSetProjectStatusPipeData,
  pipefitterRandomCompatSetState,
} from './actions';
import {
  PIPEFITTER_FETCH_PIPE_LENGTH,
  PIPEFITTER_FETCH_PROJECT_LIST,
  PIPEFITTER_GET_CONFIGURATION,
  PIPEFITTER_FETCH_PIPE_STATISTICS,
  PIPEFITTER_FETCH_PIPE_STATUS,
  PIPEFITTER_FETCH_PROJECT_SUMMARY_DATA,
  PIPEFITTER_UPLOAD_JSON_FILES,
  PIPEFITTER_CREATE_SPREAD_CONFIGURATION,
  PIPEFITTER_UPDATE_SPREAD_CONFIGURATION,
  PIPEFITTER_DELETE_SPREAD_CONFIGURATION,
  PIPEFITTER_CREATE_SUB_SPREAD_CONFIGURATION,
  PIPEFITTER_UPDATE_SUB_SPREAD_CONFIGURATION,
  PIPEFITTER_DELETE_SUB_SPREAD_CONFIGURATION,
  PIPEFITTER_SORT_SPREAD_CONFIGURATION,
  PIPEFITTER_SORT_SUB_SPREAD_CONFIGURATION,
  PIPEFITTER_FETCH_SEQUENCE_CONFIGURATION_LIST,
  PIPEFITTER_GENERATE_SEQUENCE_CONFIGURATION_LIST,
  PIPEFITTER_PIPE_BASED_GENERATE_SEQUENCE,
  PIPEFITTER_UPDATE_SPREAD_NAME,
  PIPEFITTER_DELETE_SEQUENCE_SPREAD,
  PIPEFITTER_FETCH_SEQUENCE_REPORT,
  PIPEFITTER_APPROVE_SEQUENCE_SPREAD,
  PIPEFITTER_SEND_EMAIL_SEQUENCE,
  PIPEFITTER_MIN_MAX_CALCULATION,
  PIPEFITTER_FETCH_PIPE_DATA_GRAPH,
  PIPEFITTER_FETCH_PROJECT_STATUS_PIPE_DATA,
  PIPEFITTER_FETCH_PROJECT_REPORT,
  PIPEFITTER_FETCH_PROJECT_SUMMARY_REPORT,
  PIPEFITTER_SEND_EMAIL_PIPE_DATA,
  PIPEFITTER_SEND_EMAIL_PROJECT_SUMMARY,
  PIPEFITTER_ASSIGN_PIPE_STATUS_DATA,
  PIPEFITTER_CREATE_UPDATE_PIPE_DATA,
  PIPEFITTER_DELETE_PIPE_DATA,
  PIPEFITTER_UPDATE_PIPE_STATUS,
  PIPEFITTER_GENERATE_REPORT_PIPE_DATA,
  PIPEFITTER_GENERATE_REPORT_PIPE_DATA_PIPEWISE,
  PIPEFITTER_GENERATE_REPORT_PROJECT_SUMMARY,
  PIPEFITTER_GET_ALL_PIPE_DATA,
  PIPEFITTER_TRIGGER_REPORT_COMPLIMENTARY,
  PIPEFITTER_RANDOM_COMPAT_SUBMIT,
  PIPEFITTER_RANDOM_COMPAT_RESET,
} from './types';

// Demo mode: only true when REACT_APP_PIPEFITTER_DEMO_MODE === 'true'. For production, leave unset so real APIs (configuration/spread, sequence/sequence) are used.
const DEMO_MODE = process.env.REACT_APP_PIPEFITTER_DEMO_MODE === 'true';

const RANDOM_COMPAT_POLL_MS = 3000;

function* applyCompatTicket(ticket) {
  if (ticket.status === 'done' && ticket.result) {
    // Keep last progress snapshot so Progress tab still shows completed steps after finish.
    // Only clear progress on a new Setup submit (or modal reset).
    yield put(
      pipefitterRandomCompatSetState({
        phase: 'done',
        jobId: ticket.jobId || null,
        estimatedRuns: ticket.estimatedRuns ?? null,
        ...(ticket.progress ? { progress: ticket.progress } : {}),
        result: ticket.result,
        reason: null,
        error: null,
      })
    );
    return 'done';
  }
  if (ticket.status === 'failed') {
    yield put(
      pipefitterRandomCompatSetState({
        phase: 'failed',
        jobId: ticket.jobId || null,
        progress: ticket.progress ?? null,
        result: null,
        reason: ticket.reason || 'Report failed',
        error: ticket.reason || 'Report failed',
      })
    );
    return 'failed';
  }
  yield put(
    pipefitterRandomCompatSetState({
      phase: 'working',
      jobId: ticket.jobId,
      estimatedRuns: ticket.estimatedRuns ?? null,
      progress: ticket.progress ?? null,
      result: null,
      reason: null,
      error: null,
    })
  );
  return 'working';
}

function* randomCompatSubmitSaga(action) {
  const body = action.payload || {};
  try {
    yield put(
      pipefitterRandomCompatSetState({
        phase: 'submitting',
        jobId: null,
        estimatedRuns: null,
        progress: null,
        result: null,
        reason: null,
        error: null,
        lastRequest: body,
      })
    );

    const ticket = yield call(submitCompatibilityReport, body);
    let status = yield* applyCompatTicket(ticket);
    if (status !== 'working' || !ticket.jobId) {
      return;
    }

    while (true) {
      yield delay(RANDOM_COMPAT_POLL_MS);
      const poll = yield call(getCompatibilityReport, ticket.jobId);
      status = yield* applyCompatTicket(poll);
      if (status === 'done' || status === 'failed') {
        break;
      }
    }
  } catch (error) {
    const message = extractSubmitErrorMessage(error);
    yield put(
      pipefitterRandomCompatSetState({
        phase: 'failed',
        reason: message,
        error: message,
        progress: null,
        result: null,
      })
    );
  } finally {
    if (yield cancelled()) {
      // takeLatest cancel on reset/re-submit — leave state as reset handler sets it
    }
  }
}

function* watchRandomCompatSubmit() {
  let task = null;
  while (true) {
    const action = yield take([PIPEFITTER_RANDOM_COMPAT_SUBMIT, PIPEFITTER_RANDOM_COMPAT_RESET]);
    if (task) {
      yield cancel(task);
      task = null;
    }
    if (action.type === PIPEFITTER_RANDOM_COMPAT_SUBMIT) {
      task = yield fork(randomCompatSubmitSaga, action);
    }
  }
}

/** Query string for GET /reportgen/* report endpoints (pipe data, compatibility). */
function buildReportGenQuery(payload = {}) {
  const { pipeIds, pipeId, projectId, historyId, reportName, notifyEmail, notifyEmails } = payload;
  const params = new URLSearchParams();
  if (pipeId !== undefined && pipeId !== null && pipeId !== '') {
    params.set('pipe_id', String(pipeId));
  }
  if (projectId !== undefined && projectId !== null && projectId !== '') {
    params.set('projectId', String(projectId));
  }
  if (pipeIds && pipeIds.length > 0) {
    params.set(
      'selectedPipeStatusIds',
      Array.isArray(pipeIds) ? pipeIds.join(',') : String(pipeIds),
    );
  }
  if (historyId !== undefined && historyId !== null && historyId !== '') {
    params.set('historyId', String(historyId));
  }
  if (reportName) {
    params.set('reportName', String(reportName));
  }
  if (notifyEmail) {
    params.set('notifyEmail', String(notifyEmail).trim());
  }
  if (notifyEmails && notifyEmails.length > 0) {
    params.set(
      'notifyEmails',
      Array.isArray(notifyEmails) ? notifyEmails.join(',') : String(notifyEmails),
    );
  }
  return params.toString();
}

/** JSON body for POST /reportgen/pipe-data (avoids API Gateway URL length limits). */
function buildPipeDataReportBody(payload = {}) {
  const {
    pipeIds,
    pipeId,
    projectId,
    historyId,
    reportName,
    notifyEmail,
    notifyEmails,
    groupId,
    tableLayout,
  } = payload;
  const body = {};
  if (pipeId !== undefined && pipeId !== null && pipeId !== '') {
    const n = Number(pipeId);
    body.pipe_id = Number.isFinite(n) ? n : pipeId;
  }
  if (projectId !== undefined && projectId !== null && projectId !== '') {
    const n = Number(projectId);
    body.projectId = Number.isFinite(n) ? n : projectId;
  }
  if (pipeIds && pipeIds.length > 0) {
    const ids = Array.isArray(pipeIds) ? pipeIds : String(pipeIds).split(',');
    body.selectedPipeStatusIds = ids
      .map((id) => Number(id))
      .filter((n) => Number.isFinite(n));
  }
  if (groupId !== undefined && groupId !== null) {
    body.groupId = String(groupId).trim();
  }
  if (historyId !== undefined && historyId !== null && historyId !== '') {
    const n = Number(historyId);
    body.historyId = Number.isFinite(n) ? n : historyId;
  }
  if (reportName) {
    body.reportName = String(reportName);
  }
  if (tableLayout === 'pipewise' || tableLayout === 'endwise') {
    body.tableLayout = tableLayout;
  }
  if (notifyEmail) {
    body.notifyEmail = String(notifyEmail).trim();
  }
  if (notifyEmails && notifyEmails.length > 0) {
    body.notifyEmails = Array.isArray(notifyEmails)
      ? notifyEmails.map((e) => String(e).trim()).filter(Boolean)
      : [String(notifyEmails).trim()];
  }
  return body;
}

function* fetchPipeLengthSaga({ payload }) {
  try {
    if (!payload) return;
    
    // TEMPORARY: Demo mode check - remove when real API is ready
    if (DEMO_MODE) {
      // Simulate API delay
      yield new Promise(resolve => setTimeout(resolve, 300));
      const d = DEMO_PIPE_COUNTS.data || {};
      yield put(pipefitterSetPipeCount(d.projectPipeCount));
      yield put(pipefitterSetTotalPipeCount(d.totalPipeCount));
      yield put(pipefitterSetUsedPipeCount(d.usedPipeCount));
      yield put(pipefitterSetSpreadPipeCount(d.spreadAvailablePipeCount));
      yield put(pipefitterSetSubSpreadPipeCount(d.subSpreadAvailablePipeCount));
      return;
    }
    
    const params = new URLSearchParams(payload).toString();
    const { data } = yield call(pipefitterApi.get, `/project/get-total-available-pipe/?${params}`);
    if (data?.status === 200) {
      const d = data.data || {};
      yield put(pipefitterSetPipeCount(d.projectPipeCount));
      yield put(pipefitterSetTotalPipeCount(d.totalPipeCount));
      yield put(pipefitterSetUsedPipeCount(d.usedPipeCount));
      yield put(pipefitterSetSpreadPipeCount(d.spreadAvailablePipeCount));
      yield put(pipefitterSetSubSpreadPipeCount(d.subSpreadAvailablePipeCount));
    } else if (data?.status === 410) {
      notification.warn({ message: data?.message || 'Warning', duration: 6 });
    } else {
      notification.error({ message: 'Something went wrong.' });
    }
  } catch (error) {
    notification.error({ message: error?.message || 'Failed to fetch pipe length' });
  }
}

function* fetchProjectListSaga() {
  try {
    yield put(pipefitterStartProjectLoader());
    const { data } = yield call(pipefitterApi.get, '/project');
    if (data?.status === 200) {
      yield put(pipefitterSetProjectList(data?.data?.projectList || []));
    } else if (data?.status === 410) {
      notification.warn({ message: data?.message || 'Warning', duration: 6 });
    } else {
      notification.error({ message: 'Something went wrong.' });
    }
    yield put(pipefitterStopProjectLoader());
  } catch (error) {
    yield put(pipefitterStopProjectLoader());
    notification.error({ message: error?.message || 'Failed to fetch projects' });
  }
}

function* getConfigurationSaga({ payload }) {
  if (!payload?.projectNumber) return;
  
  // TEMPORARY: Demo mode check - remove when real API is ready
  if (DEMO_MODE) {
    try {
      yield put(pipefitterSetConfigurationLoading(true));
      // Simulate API delay
      yield new Promise(resolve => setTimeout(resolve, 500));
      yield put(pipefitterSetConfigurationList(DEMO_SPREAD_CONFIGURATION.data.spreadList || []));
      yield put(pipefitterSetConfigurationLoading(false));
      return;
    } catch (error) {
      yield put(pipefitterSetConfigurationLoading(false));
      notification.error({ message: error?.message || 'Failed to fetch configuration' });
      return;
    }
  }
  
  try {
    yield put(pipefitterSetConfigurationLoading(true));
    const { data } = yield call(pipefitterApi.get, `/configuration/spread/${payload.projectNumber}`);
    if (data?.status === 200) {
      yield put(pipefitterSetConfigurationList(data?.data?.spreadList || []));
    } else if (data?.status === 410) {
      notification.warn({ message: data?.message || 'Warning', duration: 6 });
    } else {
      notification.error({ message: 'Something went wrong.' });
    }
    yield put(pipefitterSetConfigurationLoading(false));
  } catch (error) {
    yield put(pipefitterSetConfigurationLoading(false));
    notification.error({ message: error?.message || 'Failed to fetch configuration' });
  }
}

function* fetchPipeStatisticsSaga({ payload }) {
  if (!payload?.projectNumber) return;
  
  // TEMPORARY: Demo mode check - remove when real API is ready
  // if (DEMO_MODE) {
  //   try {
  //     yield put(pipefitterSetReportsLoading(true));
  //     // Simulate API delay
  //     yield new Promise(resolve => setTimeout(resolve, 500));
  //     yield put(pipefitterSetPipeStatistics(DEMO_PIPE_STATISTICS.data.pipeStats || {}));
  //     yield put(pipefitterSetReportsLoading(false));
  //     return;
  //   } catch (error) {
  //     yield put(pipefitterSetReportsLoading(false));
  //     notification.error({ message: error?.message || 'Failed to fetch pipe statistics' });
  //     return;
  //   }
  // }
  
  try {
    yield put(pipefitterSetReportsLoading(true));
    const { data } = yield call(pipefitterApi.get, `/reports/pipe-summary/${payload.projectNumber}?pipe_id=${payload.pipeId}`);
    if (data?.status === 200) {
      yield put(pipefitterSetPipeStatistics(data?.data?.pipeStats || {}));
    } else if (data?.status === 410) {
      notification.warn({ message: data?.message || 'Warning', duration: 6 });
    } else {
      notification.error({ message: 'Something went wrong.' });
    }
    yield put(pipefitterSetReportsLoading(false));
  } catch (error) {
    yield put(pipefitterSetReportsLoading(false));
    notification.error({ message: error?.message || 'Failed to fetch pipe statistics' });
  }
}

function isPipeStatusGatewayTimeoutError(error) {
  const status = Number(error?.response?.status);
  if (status === 504 || status === 502 || status === 503) return true;
  if (error?.code === 'ECONNABORTED' || error?.code === 'ERR_NETWORK') return true;
  const message = String(error?.message || '').toLowerCase();
  return (
    message.includes('504') ||
    message.includes('502') ||
    message.includes('503') ||
    message.includes('gateway timeout') ||
    message.includes('timeout')
  );
}

function* fetchPipeStatusListPage(filterPayload, skip, limit) {
  const params = buildPipeStatusListQueryParams(filterPayload, { skip, limit });
  const { data } = yield call(pipefitterApi.get, getPipeStatusListUrl(params));
  return data;
}

/** One page with a single retry on gateway timeout (deep skip is expensive on the API). */
function* fetchPipeStatusListPageWithRetry(filterPayload, skip, limit) {
  try {
    return yield call(fetchPipeStatusListPage, filterPayload, skip, limit);
  } catch (error) {
    if (!isPipeStatusGatewayTimeoutError(error)) throw error;
    yield delay(800);
    return yield call(fetchPipeStatusListPage, filterPayload, skip, limit);
  }
}

function* fetchPipeStatusSaga({ payload }) {
  const projectId = payload?.project_id ?? payload?.projectId;
  const projectNum = payload?.project_num ?? payload?.projectNumber;
  const projectName = payload?.project_name ?? payload?.projectName ?? '';
  const pipeIdRaw = payload?.pipe_id ?? payload?.pipeId;
  const fetchAllPipeSizes =
    payload?.allPipeSizes === true ||
    pipeIdRaw == null ||
    pipeIdRaw === '' ||
    String(pipeIdRaw) === '__all__';

  /** Sequential 500-row pages (API max). Parallel batches caused 504s on deep OFFSET. */
  const BATCH_SIZE = 500;
  const clientPageSize =
    payload?.limit != null && payload?.limit !== '' ? Number(payload.limit) : 25;

  if (projectNum == null || String(projectNum).trim() === '') return;

  const filterPayload = {
    project_id: projectId,
    project_num: projectNum,
    project_name: projectName,
    ...(fetchAllPipeSizes ? {} : { pipe_id: String(pipeIdRaw) }),
  };

  let loadedAnyRows = false;

  try {
    yield put(pipefitterSetReportsLoading(true));
    yield put(pipefitterSetPipeStatusLoadingMore(false));
    // Clear previous size/project rows immediately so UI does not show stale data
    // while the new pipe-status list loads.
    yield put(pipefitterSetPipeStatus({
      data: [],
      totalCount: null,
      limit: clientPageSize,
      fetchComplete: false,
      loadingMore: false,
    }));

    const firstData = yield call(
      fetchPipeStatusListPageWithRetry,
      filterPayload,
      0,
      BATCH_SIZE
    );

    if (!isGetPipeStatusListSuccess(firstData)) {
      if (firstData?.status === 410) {
        notification.warn({ message: firstData?.message || 'Warning', duration: 6 });
      } else {
        notification.error({ message: 'Something went wrong.' });
      }
      yield put(pipefitterSetPipeStatus({
        data: [],
        totalCount: 0,
        limit: clientPageSize,
        fetchComplete: true,
        loadingMore: false,
      }));
      yield put(pipefitterSetReportsLoading(false));
      return;
    }

    const { rows: firstRows, totalCount: parsedTotal } = parsePipeStatusListResponse({ data: firstData });
    const totalCount =
      parsedTotal != null && Number.isFinite(parsedTotal) ? parsedTotal : firstRows.length;
    const firstBatchComplete = firstRows.length >= totalCount || firstRows.length < BATCH_SIZE;
    loadedAnyRows = firstRows.length > 0;

    // Paint first page immediately — do not wait for remaining batches.
    yield put(pipefitterSetPipeStatus({
      data: firstRows,
      totalCount,
      limit: clientPageSize,
      fetchComplete: firstBatchComplete,
      loadingMore: !firstBatchComplete,
    }));
    yield put(pipefitterSetReportsLoading(false));

    if (firstBatchComplete) {
      yield put(pipefitterSetPipeStatusLoadingMore(false));
      return;
    }

    yield put(pipefitterSetPipeStatusLoadingMore(true));
    let skip = firstRows.length;
    let consecutiveFailures = 0;

    while (skip < totalCount) {
      try {
        const batchData = yield call(
          fetchPipeStatusListPageWithRetry,
          filterPayload,
          skip,
          BATCH_SIZE
        );

        if (!isGetPipeStatusListSuccess(batchData)) {
          consecutiveFailures += 1;
          if (consecutiveFailures >= 2) break;
          yield delay(1000);
          continue;
        }

        const { rows: batchRows } = parsePipeStatusListResponse({ data: batchData });
        if (!batchRows.length) break;

        consecutiveFailures = 0;
        skip += batchRows.length;
        const isComplete = skip >= totalCount || batchRows.length < BATCH_SIZE;

        yield put(pipefitterSetPipeStatus({
          data: batchRows,
          totalCount,
          append: true,
          fetchComplete: isComplete,
          loadingMore: !isComplete,
        }));

        if (isComplete) break;
      } catch (batchError) {
        consecutiveFailures += 1;
        if (consecutiveFailures >= 2 || !isPipeStatusGatewayTimeoutError(batchError)) {
          notification.warn({
            message:
              'Loaded part of the pipe list. Some later pages timed out — try a specific pipe size, or refresh.',
            duration: 8,
          });
          break;
        }
        yield delay(1200);
      }
    }

    yield put(pipefitterSetPipeStatus({
      fetchComplete: true,
      loadingMore: false,
      append: true,
      data: [],
      totalCount,
    }));
    yield put(pipefitterSetPipeStatusLoadingMore(false));
  } catch (error) {
    // Keep any rows already painted — do not wipe to "No data available" after a late 504.
    if (!loadedAnyRows) {
      yield put(pipefitterSetPipeStatus({
        data: [],
        totalCount: 0,
        limit: clientPageSize,
        fetchComplete: true,
        loadingMore: false,
      }));
    } else {
      yield put(pipefitterSetPipeStatusLoadingMore(false));
      yield put(
        pipefitterSetPipeStatus({
          fetchComplete: true,
          loadingMore: false,
          append: true,
          data: [],
        })
      );
      notification.warn({
        message:
          'Loaded part of the pipe list. The API timed out loading more rows (504). Try a specific pipe size.',
        duration: 8,
      });
    }
    yield put(pipefitterSetReportsLoading(false));
    yield put(pipefitterSetPipeStatusLoadingMore(false));
    if (!loadedAnyRows) {
      notification.error({
        message: isPipeStatusGatewayTimeoutError(error)
          ? 'Pipe list request timed out (504). Try again or select a specific pipe size.'
          : error?.message || 'Failed to fetch pipe status',
      });
    }
  }
}

function* fetchProjectSummaryDataSaga({ payload }) {
  if (!payload?.projectNumber) return;
  try {
    yield put(pipefitterSetReportsLoading(true));
    const { data } = yield call(pipefitterApi.get, `/reports/project-summary/${payload.projectNumber}?pipe_id=${payload.pipeId}`);
    if (data?.status === 200) {
      const raw = data?.data;
      const payload = Array.isArray(raw) && raw.length > 0 ? raw[0] : raw ?? {};
      yield put(pipefitterSetProjectSummaryData(payload));
    } else if (data?.status === 410) {
      notification.warn({ message: data?.message || 'Warning', duration: 6 });
    } else {
      notification.error({ message: 'Something went wrong.' });
    }
    yield put(pipefitterSetReportsLoading(false));
  } catch (error) {
    yield put(pipefitterSetReportsLoading(false));
    notification.error({ message: error?.message || 'Failed to fetch project summary' });
  }
}

function* uploadJsonFilesSaga(action) {
  try {
    const { projectNumber, spread, subSpread, files, cd, success, error } = action;
    
    if (!projectNumber || !files) {
      notification.error({ message: 'Missing required parameters for upload' });
      return;
    }

    const queryParams = new URLSearchParams({
      projectNumber,
      spread: spread || 'Any',
      subSpread: subSpread || 'Any',
    }).toString();

    const { data } = yield call(pipefitterApi.post, `/file-upload/upload-pipes-json?${queryParams}`, files, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    if (data?.status === 200) {
      if (data.data?.invalidFiles && data.data.invalidFiles.length > 0) {
        yield put(pipefitterSetInvalidFiles(data.data.invalidFiles));
        if (cd) {
          data.data.invalidFiles.forEach((file) => {
            cd(file.message);
          });
        }
      } else {
        yield put(pipefitterSetInvalidFiles([]));
        notification.success({
          message: 'Files Uploaded Successfully!',
          duration: 6,
        });
        if (success) success();
      }
    } else if (data?.status === 410) {
      notification.warn({
        message: data?.message || 'Warning',
        duration: 6,
      });
      if (error) error();
    } else {
      notification.error({ message: 'Something went wrong!' });
      if (error) error();
    }
  } catch (err) {
    console.error('Upload error:', err);
    notification.error({
      message: err?.response?.data?.message || err?.message || 'Failed to upload files',
    });
    if (action.error) action.error();
  }
}

function* createSpreadConfigurationSaga(action) {
  try {
    const { payload, cb, err } = action;
    yield put(pipefitterSetConfigurationLoading(true));

    const { data } = yield call(pipefitterApi.post, '/configuration/spread', payload);

    if (data?.status === 200) {
      notification.success({ message: 'Sequence created successfully.' });
      // Refresh configuration list
      yield put({ type: PIPEFITTER_GET_CONFIGURATION, payload: { projectNumber: payload.projectNumber } });
      if (cb) cb(data?.data?.id);
    } else if (data?.status === 410) {
      notification.warn({ message: data?.message || 'Warning', duration: 6 });
      if (err) err();
    } else {
      notification.error({ message: 'Something went wrong.' });
      if (err) err();
    }
    yield put(pipefitterSetConfigurationLoading(false));
  } catch (error) {
    yield put(pipefitterSetConfigurationLoading(false));
    notification.error({ message: error?.message || 'Failed to create sequence' });
    if (action.err) action.err();
  }
}

function* updateSpreadConfigurationSaga(action) {
  try {
    const { payload, cb, err } = action;
    const { spreadId, ...updateData } = payload;
    yield put(pipefitterSetConfigurationLoading(true));

    const { data } = yield call(pipefitterApi.put, `/configuration/spread/${spreadId}`, updateData);

    if (data?.status === 200) {
      notification.success({ message: 'Sequence updated successfully.' });
      yield put({ type: PIPEFITTER_GET_CONFIGURATION, payload: { projectNumber: updateData.projectNumber } });
      if (cb) cb();
    } else if (data?.status === 410) {
      notification.warn({ message: data?.message || 'Warning', duration: 6 });
      if (err) err();
    } else {
      notification.error({ message: 'Something went wrong.' });
      if (err) err();
    }
    yield put(pipefitterSetConfigurationLoading(false));
  } catch (error) {
    yield put(pipefitterSetConfigurationLoading(false));
    notification.error({ message: error?.message || 'Failed to update sequence' });
    if (action.err) action.err();
  }
}

function* deleteSpreadConfigurationSaga(action) {
  try {
    const { payload, cb, err } = action;
    yield put(pipefitterSetConfigurationLoading(true));

    const { data } = yield call(pipefitterApi.delete, `/configuration/spread/${payload.spreadId}`);

    if (data?.status === 200) {
      notification.success({ message: 'Sequence deleted successfully.' });
      yield put({ type: PIPEFITTER_GET_CONFIGURATION, payload: { projectNumber: payload.projectNumber } });
      if (cb) cb();
    } else if (data?.status === 410) {
      notification.warn({ message: data?.message || 'Warning', duration: 6 });
      if (err) err();
    } else {
      notification.error({ message: 'Something went wrong.' });
      if (err) err();
    }
    yield put(pipefitterSetConfigurationLoading(false));
  } catch (error) {
    yield put(pipefitterSetConfigurationLoading(false));
    notification.error({ message: error?.message || 'Failed to delete sequence' });
    if (action.err) action.err();
  }
}

function* createSubSpreadConfigurationSaga(action) {
  try {
    const { payload, cb, err } = action;
    yield put(pipefitterSetConfigurationLoading(true));

    const { data } = yield call(pipefitterApi.post, '/configuration/sub-spread', payload);

    if (data?.status === 200) {
      notification.success({ message: 'Sub-sequence created successfully.' });
      yield put({ type: PIPEFITTER_GET_CONFIGURATION, payload: { projectNumber: payload.projectNumber } });
      if (cb) cb(data?.data?.id);
    } else if (data?.status === 410) {
      notification.warn({ message: data?.message || 'Warning', duration: 6 });
      if (err) err();
    } else {
      notification.error({ message: 'Something went wrong.' });
      if (err) err();
    }
    yield put(pipefitterSetConfigurationLoading(false));
  } catch (error) {
    yield put(pipefitterSetConfigurationLoading(false));
    notification.error({ message: error?.message || 'Failed to create sub-sequence' });
    if (action.err) action.err();
  }
}

function* updateSubSpreadConfigurationSaga(action) {
  try {
    const { payload, cb, err } = action;
    const { subSpreadId, ...updateData } = payload;
    yield put(pipefitterSetConfigurationLoading(true));

    const { data } = yield call(pipefitterApi.put, `/configuration/sub-spread/${subSpreadId}`, updateData);

    if (data?.status === 200) {
      notification.success({ message: 'Sub-sequence updated successfully.' });
      yield put({ type: PIPEFITTER_GET_CONFIGURATION, payload: { projectNumber: updateData.projectNumber } });
      if (cb) cb();
    } else if (data?.status === 410) {
      notification.warn({ message: data?.message || 'Warning', duration: 6 });
      if (err) err();
    } else {
      notification.error({ message: 'Something went wrong.' });
      if (err) err();
    }
    yield put(pipefitterSetConfigurationLoading(false));
  } catch (error) {
    yield put(pipefitterSetConfigurationLoading(false));
    notification.error({ message: error?.message || 'Failed to update sub-sequence' });
    if (action.err) action.err();
  }
}

function* deleteSubSpreadConfigurationSaga(action) {
  try {
    const { payload, cb, err } = action;
    yield put(pipefitterSetConfigurationLoading(true));

    const { data } = yield call(pipefitterApi.delete, `/configuration/sub-spread/${payload.subSpreadId}`);

    if (data?.status === 200) {
      notification.success({ message: 'Sub-sequence deleted successfully.' });
      yield put({ type: PIPEFITTER_GET_CONFIGURATION, payload: { projectNumber: payload.projectNumber } });
      if (cb) cb();
    } else if (data?.status === 410) {
      notification.warn({ message: data?.message || 'Warning', duration: 6 });
      if (err) err();
    } else {
      notification.error({ message: 'Something went wrong.' });
      if (err) err();
    }
    yield put(pipefitterSetConfigurationLoading(false));
  } catch (error) {
    yield put(pipefitterSetConfigurationLoading(false));
    notification.error({ message: error?.message || 'Failed to delete sub-sequence' });
    if (action.err) action.err();
  }
}

function* sortSpreadConfigurationSaga(action) {
  try {
    const { payload, cb, err } = action;
    yield put(pipefitterSetConfigurationLoading(true));

    const { data } = yield call(pipefitterApi.post, '/configuration/sort/spread', payload);

    if (data?.status === 200) {
      notification.success({ message: 'Sequence sorted successfully.' });
      yield put({ type: PIPEFITTER_GET_CONFIGURATION, payload: { projectNumber: payload.projectNumber } });
      if (cb) cb();
    } else if (data?.status === 410) {
      notification.warn({ message: data?.message || 'Warning', duration: 6 });
      if (err) err();
    } else {
      notification.error({ message: 'Something went wrong.' });
      if (err) err();
    }
    yield put(pipefitterSetConfigurationLoading(false));
  } catch (error) {
    yield put(pipefitterSetConfigurationLoading(false));
    notification.error({ message: error?.message || 'Failed to sort sequences' });
    if (action.err) action.err();
  }
}

function* sortSubSpreadConfigurationSaga(action) {
  try {
    const { payload, cb, err } = action;
    yield put(pipefitterSetConfigurationLoading(true));

    const { projectNumber, ...sortData } = payload;
    const { data } = yield call(pipefitterApi.post, '/configuration/sort/sub-spread', sortData);

    if (data?.status === 200) {
      notification.success({ message: 'Sub-sequence sorted successfully.' });
      yield put({ type: PIPEFITTER_GET_CONFIGURATION, payload: { projectNumber } });
      if (cb) cb();
    } else if (data?.status === 410) {
      notification.warn({ message: data?.message || 'Warning', duration: 6 });
      if (err) err();
    } else {
      notification.error({ message: 'Something went wrong.' });
      if (err) err();
    }
    yield put(pipefitterSetConfigurationLoading(false));
  } catch (error) {
    yield put(pipefitterSetConfigurationLoading(false));
    notification.error({ message: error?.message || 'Failed to sort sub-sequences' });
    if (action.err) action.err();
  }
}

function* fetchSequenceListSaga(action) {
  const BATCH_SIZE = 250;

  try {
    const { payload, cb } = action;
    yield put(pipefitterSetSequenceLoading(true));
    yield put(pipefitterSetSequenceLoadingMore(false));

    // TEMPORARY: Demo mode check - remove when real API is ready
    if (DEMO_MODE) {
      yield new Promise((resolve) => setTimeout(resolve, 500));
      const demoList = DEMO_SEQUENCE_LIST.data.recordList || [];
      yield put(pipefitterSetSequenceList({
        data: demoList,
        totalCount: demoList.length,
        fetchComplete: true,
        loadingMore: false,
      }));
      if (cb) cb(DEMO_SEQUENCE_LIST.data);
      yield put(pipefitterSetSequenceLoading(false));
      return;
    }

    const baseParams = new URLSearchParams();
    if (payload?.projectNumber != null) baseParams.set('projectNumber', String(payload.projectNumber));
    if (payload?.spread != null) baseParams.set('spread', String(payload.spread));
    if (payload?.subSpread != null) baseParams.set('subSpread', String(payload.subSpread));

    const firstParams = new URLSearchParams(baseParams);
    firstParams.set('skip', '0');
    firstParams.set('limit', String(BATCH_SIZE));

    const { data } = yield call(pipefitterApi.get, `/sequence/sequence?${firstParams.toString()}`);

    if (data?.status === 200) {
      const firstRows = data?.data?.recordList || [];
      const totalCount = Number(data?.data?.count ?? firstRows.length) || firstRows.length;
      const firstBatchComplete = firstRows.length >= totalCount || firstRows.length < BATCH_SIZE;

      yield put(pipefitterSetSequenceList({
        data: firstRows,
        totalCount,
        fetchComplete: firstBatchComplete,
        loadingMore: !firstBatchComplete,
      }));
      if (cb) cb({ ...data?.data, recordList: firstRows, count: totalCount });
      yield put(pipefitterSetSequenceLoading(false));

      if (firstBatchComplete) {
        yield put(pipefitterSetSequenceLoadingMore(false));
        return;
      }

      yield put(pipefitterSetSequenceLoadingMore(true));
      let skip = firstRows.length;

      while (skip < totalCount) {
        const batchParams = new URLSearchParams(baseParams);
        batchParams.set('skip', String(skip));
        batchParams.set('limit', String(BATCH_SIZE));
        const { data: batchData } = yield call(pipefitterApi.get, `/sequence/sequence?${batchParams.toString()}`);

        if (batchData?.status !== 200) break;

        const batchRows = batchData?.data?.recordList || [];
        if (!batchRows.length) break;

        skip += batchRows.length;
        const isComplete = skip >= totalCount || batchRows.length < BATCH_SIZE;

        yield put(pipefitterSetSequenceList({
          data: batchRows,
          totalCount,
          append: true,
          fetchComplete: isComplete,
          loadingMore: !isComplete,
        }));

        if (isComplete) break;
      }

      yield put(pipefitterSetSequenceLoadingMore(false));
    } else if (data?.status === 410) {
      notification.warn({ message: data?.message || 'Warning', duration: 6 });
      yield put(pipefitterSetSequenceList({ data: [], totalCount: 0, fetchComplete: true, loadingMore: false }));
      if (cb) cb({ recordList: [], count: 0 });
      yield put(pipefitterSetSequenceLoading(false));
    } else {
      notification.error({ message: 'Something went wrong.' });
      yield put(pipefitterSetSequenceList({ data: [], totalCount: 0, fetchComplete: true, loadingMore: false }));
      if (cb) cb({ recordList: [], count: 0 });
      yield put(pipefitterSetSequenceLoading(false));
    }
  } catch (error) {
    yield put(pipefitterSetSequenceLoading(false));
    yield put(pipefitterSetSequenceLoadingMore(false));
    notification.error({ message: error?.message || 'Failed to fetch sequence list' });
    if (action.cb) action.cb({ recordList: [], count: 0 });
  }
}

function* generateSequenceSaga(action) {
  try {
    const { payload, cb, error } = action;
    yield put(pipefitterSetSequenceLoading(true));

    const { data } = yield call(pipefitterApi.post, '/sequence/generate-sequence', payload);

    if (data?.status === 200) {
      notification.success({ message: 'Sequence Generated Successfully.' });
      yield put({ type: PIPEFITTER_GET_CONFIGURATION, payload: { projectNumber: payload.projectNumber } });
      if (cb) cb(data?.data);
    } else if (data?.status === 410) {
      notification.warn({ message: data?.message || 'Warning', duration: 6 });
      if (error) error();
    } else {
      notification.error({ message: 'Something went wrong.' });
      if (error) error();
    }
    yield put(pipefitterSetSequenceLoading(false));
  } catch (err) {
    yield put(pipefitterSetSequenceLoading(false));
    notification.error({ message: err?.message || 'Failed to generate sequence' });
    if (action.error) action.error();
  }
}

function isGatewayTimeoutStatus(status) {
  const code = Number(status);
  return code === 504 || code === 502 || code === 503;
}

function isAsyncGatewayTimeout(err) {
  const httpStatus = Number(err?.response?.status ?? err?.status ?? NaN);
  const bodyStatus =
    err && typeof err === 'object' && !err.response ? Number(err.status) : NaN;

  if (isGatewayTimeoutStatus(httpStatus) || isGatewayTimeoutStatus(bodyStatus)) {
    return true;
  }
  if (err?.code === 'ECONNABORTED' || err?.code === 'ERR_NETWORK') return true;
  const message = String(err?.message || err?.name || (typeof err === 'string' ? err : '')).toLowerCase();
  return (
    message === 'network error' ||
    message.includes('504') ||
    message.includes('502') ||
    message.includes('503') ||
    message.includes('gateway timeout') ||
    message.includes('timeout') ||
    message.includes('timed out') ||
    message.includes('status code 504') ||
    message.includes('status code 502') ||
    message.includes('status code 503')
  );
}

function isAsyncGatewayAcceptedResponse(response) {
  return isGatewayTimeoutStatus(Number(response?.status ?? NaN));
}

function* pipeBasedGenerateSequenceStarted(action, payload, resultData) {
  notification.success({ message: 'Sequence generation started' });
  yield put({ type: PIPEFITTER_GET_CONFIGURATION, payload: { projectNumber: payload.projectNumber } });
  if (action.cb) action.cb(resultData);
}

function* pipeBasedGenerateSequenceSaga(action) {
  try {
    const { payload, error } = action;
    yield put(pipefitterSetSequenceLoading(true));

    const response = yield call(pipefitterApi.post, '/sequence/pipe-based-generate', payload, {
      validateStatus: () => true,
    });
    const data = response?.data;
    const httpStatus = Number(response?.status ?? 0);

    if (isAsyncGatewayAcceptedResponse(response)) {
      yield* pipeBasedGenerateSequenceStarted(action, payload);
    } else if (httpStatus < 200 || httpStatus >= 300) {
      notification.error({ message: data?.message || 'Something went wrong.' });
      if (error) error();
    } else if (data?.status === 410) {
      notification.warn({ message: data?.message || 'Warning', duration: 6 });
      if (error) error();
    } else if (data?.success === 0) {
      notification.error({ message: data?.message || 'Something went wrong.' });
      if (error) error();
    } else {
      yield* pipeBasedGenerateSequenceStarted(action, payload, data?.data);
    }
    yield put(pipefitterSetSequenceLoading(false));
  } catch (err) {
    yield put(pipefitterSetSequenceLoading(false));
    if (isAsyncGatewayTimeout(err)) {
      yield* pipeBasedGenerateSequenceStarted(action, action.payload);
    } else {
      notification.error({ message: err?.message || 'Failed to generate sequence' });
      if (action.error) action.error();
    }
  }
}

function* updateSpreadNameSaga(action) {
  try {
    const { payload, cb, err } = action;
    const { spreadId, sequenceName, projectNumber } = payload;
    yield put(pipefitterSetConfigurationLoading(true));

    const response = yield call(pipefitterApi.patch, `/sequence/spread/${spreadId}`, {
      sequenceName: sequenceName?.trim() || '',
      projectNumber,
    });
    const data = response?.data;
    const isSuccess = data?.status === 200 || response?.status === 200;

    if (isSuccess) {
      notification.success({ message: data?.message || 'Spread name updated' });
      yield put({ type: PIPEFITTER_GET_CONFIGURATION, payload: { projectNumber } });
      if (cb) cb();
    } else if (data?.status === 410) {
      notification.warn({ message: data?.message || 'Warning', duration: 6 });
      if (err) err();
    } else {
      const message = data?.message || 'Something went wrong.';
      notification.error({ message });
      if (err) err();
    }
    yield put(pipefitterSetConfigurationLoading(false));
  } catch (error) {
    yield put(pipefitterSetConfigurationLoading(false));
    const errData = error?.response?.data;
    const message = (typeof errData === 'string' ? errData : errData?.message) || error?.message || 'Failed to update spread name';
    notification.error({ message });
    if (action.err) action.err();
  }
}

function* deleteSequenceSaga(action) {
  try {
    const { payload, cb, error } = action;
    yield put(pipefitterSetSequenceLoading(true));

    // Axios delete with body data
    const { data } = yield call(pipefitterApi.delete, '/sequence/sequence', {
      data: payload,
    });

    if (data?.status === 200) {
      notification.success({
        message: `${payload.sequenceDeletionFor} sequence deleted successfully`,
      });
      yield put({ type: PIPEFITTER_GET_CONFIGURATION, payload: { projectNumber: payload.projectNumber } });
      if (cb) cb();
    } else if (data?.status === 410) {
      notification.warn({ message: data?.message || 'Warning', duration: 6 });
      if (error) error();
    } else {
      notification.error({ message: 'Something went wrong.' });
      if (error) error();
    }
    yield put(pipefitterSetSequenceLoading(false));
  } catch (err) {
    yield put(pipefitterSetSequenceLoading(false));
    notification.error({ message: err?.message || 'Failed to delete sequence' });
    if (action.error) action.error();
  }
}

function* fetchSequenceReportSaga(action) {
  const { payload, cb, error: errorCb } = action;
  const { skipSequenceTableLoading, ...reportQueryPayload } = payload || {};
  const useTableLoading = !skipSequenceTableLoading;

  try {
    if (useTableLoading) {
      yield put(pipefitterSetSequenceLoading(true));
    }

    const queryParams = new URLSearchParams(reportQueryPayload).toString();
    const response = yield call(pipefitterApi.get, `/reportgen/sequence?${queryParams}`, {
      responseType: 'blob',
    });

    if (response.status === 200 || response.data) {
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: 'application/pdf' });
      if (cb) cb(blob);
    } else {
      notification.error({ message: 'Failed to download sequence report' });
      if (errorCb) errorCb();
    }
    if (useTableLoading) {
      yield put(pipefitterSetSequenceLoading(false));
    }
  } catch (err) {
    if (useTableLoading) {
      yield put(pipefitterSetSequenceLoading(false));
    }
    notification.error({ message: err?.message || 'Failed to fetch sequence report' });
    if (errorCb) errorCb(err);
  }
}

function* approveSequenceSaga(action) {
  try {
    const { payload, cb, error } = action;
    yield put(pipefitterSetSequenceLoading(true));

    const { data } = yield call(pipefitterApi.post, '/sequence/approve-sequence', payload);

    if (data?.status === 200) {
      notification.success({ message: 'Sequence approved successfully' });
      yield put({ type: PIPEFITTER_GET_CONFIGURATION, payload: { projectNumber: payload.projectNumber } });
      if (cb) cb();
    } else if (data?.status === 410) {
      notification.warn({ message: data?.message || 'Warning', duration: 6 });
      if (error) error();
    } else {
      notification.error({ message: 'Something went wrong.' });
      if (error) error();
    }
    yield put(pipefitterSetSequenceLoading(false));
  } catch (err) {
    yield put(pipefitterSetSequenceLoading(false));
    notification.error({ message: err?.message || 'Failed to approve sequence' });
    if (action.error) action.error();
  }
}

function* sendEmailSequenceSaga(action) {
  try {
    const { payload, cb, error } = action;
    yield put(pipefitterSetSequenceLoading(true));

    const { data } = yield call(pipefitterApi.post, '/sequence/send-email/sequence', payload);

    if (data?.status === 200) {
      notification.success({ message: 'Email sent successfully', duration: 6 });
      if (cb) cb();
    } else if (data?.status === 410) {
      notification.warn({ message: data?.message || 'Warning', duration: 6 });
      if (error) error();
    } else {
      notification.error({ message: 'Something went wrong.' });
      if (error) error();
    }
    yield put(pipefitterSetSequenceLoading(false));
  } catch (err) {
    yield put(pipefitterSetSequenceLoading(false));
    notification.error({ message: err?.message || 'Failed to send email' });
    if (action.error) action.error();
  }
}

function* minMaxCalculationSaga(action) {
  const { payload, cb, error } = action;
  const projectNumber = String(payload?.projectNumber || '').trim();

  if (!projectNumber) {
    notification.error({ message: 'Project number is required' });
    if (error) error();
    return;
  }

  const body = {};
  if (payload.groupId !== undefined && payload.groupId !== null) {
    body.groupId = String(payload.groupId).trim();
  }

  // Fire-and-forget: do not wait for response / 504. Modal closes immediately.
  pipefitterApi
    .post(`/sequence/calculate-min-max-sequence/${projectNumber}`, body, {
      validateStatus: () => true,
    })
    .catch(() => {
      // Ignore timeout / network errors — calculation continues server-side
    });

  notification.success({ message: 'Calculation started' });
  // cb closes modal + sets InProgress (status checked again when modal reopens)
  if (cb) cb();
}

function* fetchPipeDataGraphSaga(action) {
  try {
    const { payload, cb } = action;
    yield put(pipefitterSetReportsLoading(true));

    const { data } = yield call(pipefitterApi.get, `/reports/pipe-data/${payload.projectNumber}?pipe_id=${payload.pipeId}`);
    // const { data } = yield call(pipefitterApi.get, `/reports/pipe-data/N-3358`);

    if (data?.status === 200) {
      yield put(pipefitterSetPipeDataGraph(data?.data?.pipeList || []));
      if (cb) cb();
    } else if (data?.status === 410) {
      notification.warn({ message: data?.message || 'Warning', duration: 6 });
      if (cb) cb();
    } else {
      notification.error({ message: 'Something went wrong.' });
      if (cb) cb();
    }
    yield put(pipefitterSetReportsLoading(false));
  } catch (error) {
    yield put(pipefitterSetReportsLoading(false));
    notification.error({ message: error?.message || 'Failed to fetch pipe data graph' });
    if (action.cb) action.cb();
  }
}

function* fetchProjectStatusPipeDataSaga(action) {
  try {
    const { payload, cb } = action;
    yield put(pipefitterSetReportsLoading(true));

    // // TEMPORARY: Demo mode check - remove when real API is ready
    // if (DEMO_MODE) {
    //   // Simulate API delay
    //   yield new Promise(resolve => setTimeout(resolve, 500));
    //   yield put(pipefitterSetProjectStatusPipeData(DEMO_PIPE_SCAN_DATA.data.pipeList || []));
    //   if (cb) cb();
    //   yield put(pipefitterSetReportsLoading(false));
    //   return;
    // }

    console.log('payload', payload);

    const { data } = yield call(pipefitterApi.get, `/reports/pipe-scan/${payload.projectNumber}?pipe_id=${payload.pipeId}`);

    if (data?.status === 200) {
      yield put(pipefitterSetProjectStatusPipeData(data?.data?.pipeList || []));
      if (cb) cb();
    } else if (data?.status === 410) {
      notification.warn({ message: data?.message || 'Warning', duration: 6 });
      if (cb) cb();
    } else {
      notification.error({ message: 'Something went wrong.' });
      if (cb) cb();
    }
    yield put(pipefitterSetReportsLoading(false));
  } catch (error) {
    yield put(pipefitterSetReportsLoading(false));
    notification.error({ message: error?.message || 'Failed to fetch project status pipe data' });
    if (action.cb) action.cb();
  }
}

function* fetchProjectReportSaga(action) {
  try {
    const { payload, cb } = action;
    yield put(pipefitterSetReportsLoading(true));

    const response = yield call(pipefitterApi.get, `/reportgen/pipe-data/N-3358`, {
      responseType: 'blob',
    });

    if (response.status === 200 || response.data) {
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: 'application/pdf' });
      if (cb) cb(blob);
    } else {
      notification.error({ message: 'Failed to download project report' });
    }
    yield put(pipefitterSetReportsLoading(false));
  } catch (err) {
    yield put(pipefitterSetReportsLoading(false));
    notification.error({ message: err?.message || 'Failed to fetch project report' });
  }
}

function* fetchProjectSummaryReportSaga(action) {
  try {
    const { payload, cb } = action;
    const { projectNumber, pipeIds, pipeId, historyId, reportName } = payload || {};
    yield put(pipefitterSetReportsLoading(true));

    const query = buildReportGenQuery({ pipeIds, pipeId, historyId, reportName });
    const url = `/reportgen/complimentary-report/${projectNumber || ''}${query ? `?${query}` : ''}`;
    const response = yield call(pipefitterApi.get, url, { responseType: 'blob' });

    if (response.status === 200 || response.data) {
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: 'application/pdf' });
      if (cb) cb(blob);
    } else {
      notification.error({ message: 'Failed to download pipe compatibility report' });
    }
    yield put(pipefitterSetReportsLoading(false));
  } catch (err) {
    yield put(pipefitterSetReportsLoading(false));
    notification.error({ message: err?.message || 'Failed to fetch pipe compatibility report' });
  }
}

function* sendEmailPipeDataSaga(action) {
  try {
    const { payload, cb, error } = action;
    yield put(pipefitterSetReportsLoading(true));

    const { data } = yield call(pipefitterApi.post, `/reports/send-email/pipe-data/N-3358`, {
      emails: payload.emails ?? [],
    });

    if (data?.status === 200) {
      notification.success({ message: 'Email sent successfully', duration: 6 });
      if (cb) cb();
    } else if (data?.status === 410) {
      notification.warn({ message: data?.message || 'Warning', duration: 6 });
      if (error) error();
    } else {
      notification.error({ message: 'Something went wrong.' });
      if (error) error();
    }
    yield put(pipefitterSetReportsLoading(false));
  } catch (err) {
    yield put(pipefitterSetReportsLoading(false));
    notification.error({ message: err?.message || 'Failed to send email' });
    if (action.error) action.error();
  }
}

function* sendEmailProjectSummarySaga(action) {
  try {
    const { payload, cb, error } = action;
    yield put(pipefitterSetReportsLoading(true));

    const { data } = yield call(pipefitterApi.post, `/reports/send-email/project-summary/N-3358`, {
      emails: payload.emails ?? [],
    });

    if (data?.status === 200) {
      notification.success({ message: 'Email sent successfully', duration: 6 });
      if (cb) cb();
    } else if (data?.status === 410) {
      notification.warn({ message: data?.message || 'Warning', duration: 6 });
      if (error) error();
    } else {
      notification.error({ message: 'Something went wrong.' });
      if (error) error();
    }
    yield put(pipefitterSetReportsLoading(false));
  } catch (err) {
    yield put(pipefitterSetReportsLoading(false));
    notification.error({ message: err?.message || 'Failed to send email' });
    if (action.error) action.error();
  }
}

function* assignPipeStatusDataSaga(action) {
  try {
    const { projectNumber, payload, cb, callback } = action;
    yield put(pipefitterSetReportsLoading(true));

    // const { data } = yield call(pipefitterApi.put, `/reports/pipe-status/${projectNumber}`, {
      const { data } = yield call(pipefitterApi.put, `/reports/pipe-status/N-3358`, {
      updatePipeStatusPayload: payload,
    });

    if (data?.status === 200) {
      notification.success({ message: 'Data assigned successfully', duration: 6 });
      if (cb) cb();
      if (callback) callback();
    } else if (data?.status === 410) {
      notification.warn({ message: data?.message || 'Warning', duration: 6 });
    } else {
      notification.error({ message: 'Something went wrong.' });
    }
    yield put(pipefitterSetReportsLoading(false));
  } catch (err) {
    yield put(pipefitterSetReportsLoading(false));
    notification.error({ message: err?.message || 'Failed to assign pipe status' });
  }
}

function* createUpdatePipeDataSaga(action) {
  try {
    const { payload, cb, err } = action;
    yield put(pipefitterSetReportsLoading(true));

    const body = Array.isArray(payload) ? payload : [payload];
    const { data } = yield call(pipefitterApi.post, '/reports/createUpdatePipeData', { items: body });

    if (data?.status === 200) {
      notification.success({ message: 'Pipe data created successfully.' });
      if (cb) cb(data?.data);
    } else if (data?.status === 410) {
      notification.warn({ message: data?.message || 'Warning', duration: 6 });
      if (err) err();
    } else {
      notification.error({ message: 'Something went wrong.' });
      if (err) err();
    }
    yield put(pipefitterSetReportsLoading(false));
  } catch (error) {
    yield put(pipefitterSetReportsLoading(false));
    notification.error({ message: error?.message || 'Failed to create update pipe data' });
    if (action.err) action.err();
  }
}

function* deletePipeDataSaga(action) {
  try {
    const { payload, cb, err } = action;
    const { projectNumber, pipeIds } = payload || {};
    yield put(pipefitterSetReportsLoading(true));

    const response = yield call(pipefitterApi.post, '/reports/soft-delete/pipe-data', {
      ids: pipeIds || [],
      isset: 1,
    });
    const data = response?.data;

    if (data?.status === 200) {
      notification.success({ message: 'Selected pipe data deleted successfully.' });
      if (cb) cb(data?.data);
    } else if (data?.status === 410) {
      notification.warn({ message: data?.message || 'Warning', duration: 6 });
      if (err) err();
    } else {
      notification.error({ message: 'Something went wrong.' });
      if (err) err();
    }
    yield put(pipefitterSetReportsLoading(false));
  } catch (error) {
    yield put(pipefitterSetReportsLoading(false));
    notification.error({ message: error?.message || 'Failed to delete pipe data' });
    if (action.err) action.err();
  }
}

function* updatePipeStatusSaga(action) {
  try {
    const { payload, cb, err } = action;
    const { id, status } = payload || {};
    if (id == null || status == null) return;
    yield put(pipefitterSetReportsLoading(true));
    const { data } = yield call(pipefitterApi.put, '/reports/update-pipe-status', { id: Number(id), status });
    const isSuccess = data?.status === 200 || data?.success === 1;
    if (isSuccess) {
      notification.success({ message: 'Pipe status updated successfully.' });
      if (cb) cb(data?.data);
    } else if (data?.status === 410) {
      notification.warn({ message: data?.message || 'Warning', duration: 6 });
      if (err) err();
    } else {
      notification.error({ message: data?.message || 'Something went wrong.' });
      if (err) err();
    }
    yield put(pipefitterSetReportsLoading(false));
  } catch (error) {
    yield put(pipefitterSetReportsLoading(false));
    notification.error({ message: error?.message || 'Failed to update pipe status' });
    if (action.err) action.err();
  }
}

function* pipeDataReportTriggered(action, notifyEmail, responseData) {
  const apiMessage =
    typeof responseData === 'string'
      ? responseData
      : responseData?.message ?? '';
  const emailSuffix = notifyEmail
    ? ` The PDF will be emailed to ${notifyEmail} when it is ready.`
    : ' The PDF will be emailed when it is ready.';
  notification.success({
    message: 'Pipe data report generation started',
    description: apiMessage || `Your pipe data report is being generated.${emailSuffix}`,
    duration: 6,
  });
  if (action.cb) action.cb(responseData);
}

function* generateReportPipeDataSaga(action) {
  const { payload, cb, err } = action;
  const {
    projectNumber,
    pipeIds,
    pipeId,
    projectId,
    historyId,
    reportName,
    notifyEmail,
    notifyEmails,
    groupId,
  } = payload || {};

  const url = `/reportgen/pipe-data/${projectNumber || ''}`;
  const body = buildPipeDataReportBody({
    pipeIds,
    pipeId,
    projectId,
    historyId,
    reportName,
    notifyEmail,
    notifyEmails,
    groupId,
  });

  if (notifyEmail || (notifyEmails && notifyEmails.length > 0)) {
    try {
      const response = yield call(pipefitterApi.post, url, body, { validateStatus: () => true });
      const status = Number(response?.status ?? 0);
      const data = response?.data;

      if (isAsyncGatewayAcceptedResponse(response)) {
        yield* pipeDataReportTriggered(action, notifyEmail);
      } else if (status >= 200 && status < 300) {
        yield* pipeDataReportTriggered(action, notifyEmail, data);
      } else {
        notification.error({
          message: (typeof data === 'object' && data?.message) || 'Failed to start pipe data report',
        });
        if (err) err();
      }
    } catch (error) {
      if (isAsyncGatewayTimeout(error)) {
        yield* pipeDataReportTriggered(action, notifyEmail);
      } else {
        notification.error({ message: error?.message || 'Failed to start pipe data report' });
        if (err) err();
      }
    }
    return;
  }

  try {
    // Sync PDF download path still uses GET query for small selections / legacy callers.
    const query = buildReportGenQuery({
      pipeIds,
      pipeId,
      projectId,
      historyId,
      reportName,
      notifyEmail,
      notifyEmails,
    });
    const getUrl = `${url}${query ? `?${query}` : ''}`;
    const response = yield call(pipefitterApi.get, getUrl, { responseType: 'blob' });

    if (response?.status === 200 || response?.data) {
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: 'application/pdf' });
      if (cb) cb(blob);
    } else {
      notification.error({ message: 'Failed to generate report' });
      if (err) err();
    }
  } catch (error) {
    notification.error({ message: error?.message || 'Failed to generate report' });
    if (err) err();
  }
}

function* pipeDataPipewiseReportTriggered(action, notifyEmail, responseData) {
  const apiMessage =
    typeof responseData === 'string'
      ? responseData
      : responseData?.message ?? '';
  const emailSuffix = notifyEmail
    ? ` It will be sent to ${notifyEmail} by email when ready.`
    : ' It will be sent by email when ready.';
  notification.success({
    message: 'Pipewise pipe data report generation started',
    description:
      apiMessage || `Your pipewise pipe data report is being generated.${emailSuffix}`,
    duration: 6,
  });
  if (action.cb) action.cb(responseData);
}

/** Same as classic pipe-data generate, but hits /pipe-data-pipewise (one row per pipe ID). */
function* generateReportPipeDataPipewiseSaga(action) {
  const { payload, cb, err } = action;
  const {
    projectNumber,
    pipeIds,
    pipeId,
    projectId,
    historyId,
    reportName,
    notifyEmail,
    notifyEmails,
    groupId,
  } = payload || {};

  const url = `/reportgen/pipe-data-pipewise/${projectNumber || ''}`;
  const body = buildPipeDataReportBody({
    pipeIds,
    pipeId,
    projectId,
    historyId,
    reportName: reportName || 'Pipe Data Report (Pipewise)',
    notifyEmail,
    notifyEmails,
    groupId,
    tableLayout: 'pipewise',
  });

  if (notifyEmail || (notifyEmails && notifyEmails.length > 0)) {
    try {
      const response = yield call(pipefitterApi.post, url, body, { validateStatus: () => true });
      const status = Number(response?.status ?? 0);
      const data = response?.data;

      if (isAsyncGatewayAcceptedResponse(response)) {
        yield* pipeDataPipewiseReportTriggered(action, notifyEmail);
      } else if (status >= 200 && status < 300) {
        yield* pipeDataPipewiseReportTriggered(action, notifyEmail, data);
      } else {
        notification.error({
          message:
            (typeof data === 'object' && data?.message) ||
            'Failed to start pipewise pipe data report',
        });
        if (err) err();
      }
    } catch (error) {
      if (isAsyncGatewayTimeout(error)) {
        yield* pipeDataPipewiseReportTriggered(action, notifyEmail);
      } else {
        notification.error({
          message: error?.message || 'Failed to start pipewise pipe data report',
        });
        if (err) err();
      }
    }
    return;
  }

  try {
    const query = buildReportGenQuery({
      pipeIds,
      pipeId,
      projectId,
      historyId,
      reportName: reportName || 'Pipe Data Report (Pipewise)',
      notifyEmail,
      notifyEmails,
    });
    const getUrl = `${url}${query ? `?${query}` : ''}`;
    const response = yield call(pipefitterApi.get, getUrl, { responseType: 'blob' });

    if (response?.status === 200 || response?.data) {
      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: 'application/pdf' });
      if (cb) cb(blob);
    } else {
      notification.error({ message: 'Failed to generate pipewise report' });
      if (err) err();
    }
  } catch (error) {
    notification.error({ message: error?.message || 'Failed to generate pipewise report' });
    if (err) err();
  }
}
function* generateReportProjectSummarySaga(action) {
  try {
    const { payload, cb, err } = action;
    const { projectNumber, pipeIds, pipeId, historyId, reportName } = payload || {};

    const query = buildReportGenQuery({ pipeIds, pipeId, historyId, reportName });
    const url = `/reportgen/complimentary-report/${projectNumber || ''}${query ? `?${query}` : ''}`;
    const response = yield call(pipefitterApi.get, url, { responseType: 'blob' });

    if (response?.status === 200 || response?.data) {
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: 'application/pdf' });
      if (cb) cb(blob);
    } else {
      notification.error({ message: 'Failed to generate report' });
      if (err) err();
    }
  } catch (error) {
    notification.error({ message: error?.message || 'Failed to generate report' });
    if (action.err) action.err();
  }
}

function* complimentaryReportTriggered(action, notifyEmail, responseData) {
  const apiMessage =
    typeof responseData === 'string'
      ? responseData
      : responseData?.message ?? '';
  const emailSuffix = notifyEmail
    ? ` It will be sent to ${notifyEmail} by email when ready.`
    : ' It will be sent by email when ready.';
  notification.success({
    message: 'Report generation started',
    description: apiMessage || `The pipe compatibility report is being generated.${emailSuffix}`,
    duration: 6,
  });
  if (action.cb) action.cb(responseData);
}

/** Trigger complimentary report on the server. Expects a quick HTTP success (job started), not a PDF blob — avoids blocking the UI on long generation. */
function* triggerReportComplimentarySaga(action) {
  try {
    const { payload, err } = action;
    const { projectNumber, pipeIds, pipeId, historyId, reportName, notifyEmail, notifyEmails } = payload || {};

    const query = buildReportGenQuery({
      pipeIds,
      pipeId,
      historyId,
      reportName,
      notifyEmail,
    });
    const url = `/reportgen/complimentary-report/${projectNumber || ''}${query ? `?${query}` : ''}`;
    const response = yield call(pipefitterApi.get, url, { validateStatus: () => true });
    const status = Number(response?.status ?? 0);
    const data = response?.data;

    if (isAsyncGatewayAcceptedResponse(response)) {
      yield* complimentaryReportTriggered(action, notifyEmail);
    } else if (status >= 200 && status < 300) {
      yield* complimentaryReportTriggered(action, notifyEmail, data);
    } else {
      notification.error({
        message: (typeof data === 'object' && data?.message) || 'Failed to trigger complimentary report',
      });
      if (err) err();
    }
  } catch (error) {
    if (isAsyncGatewayTimeout(error)) {
      yield* complimentaryReportTriggered(action, action.payload?.notifyEmail);
    } else {
      notification.error({ message: error?.message || 'Failed to trigger complimentary report' });
      if (action.err) action.err();
    }
  }
}
function* getAllPipeDataSaga(action) {
  try {
    const { payload, cb, err } = action;
    const { projectNumber, pipeId, selectedPipeIds, reportHistory } = payload || {};

    yield put(pipefitterSetReportsLoading(true));

    let body;
    if (reportHistory) {
      const response = yield call(pipefitterApi.post, '/reportgen/report-history', reportHistory);
      body = response?.data;
    } else {
      const params = new URLSearchParams();
      if (pipeId) params.set('pipe_id', pipeId);
      if (selectedPipeIds && selectedPipeIds.length > 0) {
        params.set('selectedPipeStatusIds', Array.isArray(selectedPipeIds) ? selectedPipeIds.join(',') : selectedPipeIds);
      }

      const query = params.toString();
      const url = `/reportgen/all-data-Pipe-Report-Data/${projectNumber || ''}?${query}`;
      const response = yield call(pipefitterApi.get, url);
      body = response?.data;
    }

    const ok = body?.status === 200 || body?.success === 1;

    if (ok && body?.data) {
      const raw = body.data;
      const d = Array.isArray(raw) ? raw[0] : raw;
      const extracted = reportHistory
        ? extractPipeDataFromReportHistory(d)
        : null;

      const nested =
        extracted?.reportData ||
        (d?.reportData && typeof d.reportData === 'object'
          ? d.reportData
          : d?.visualizationData && typeof d.visualizationData === 'object'
            ? d.visualizationData
            : d);

      yield put(
        pipefitterSetPipesListFiltered(
          extracted?.pipesListFiltered ||
            nested?.pipesList ||
            nested?.pipesListFiltered ||
            d?.pipesListFiltered ||
            []
        )
      );
      yield put(pipefitterSetProjectStatusPipeData(nested?.pipeScan || d?.pipeScan || []));
      yield put(
        pipefitterSetPipeStatistics(
          extracted?.pipeStatistics ||
            nested?.pipeStats ||
            nested?.pipeSummary?.pipeStats ||
            nested?.pipeSummary ||
            d?.pipeSummary?.pipeStats ||
            d?.pipeSummary ||
            {}
        )
      );
      yield put(pipefitterSetProjectSummaryData(nested?.pipeCompatibility || d?.pipeCompatibility || {}));
      yield put(
        pipefitterSetPipeDataGraph(
          extracted?.pipeDataGraph ||
            nested?.graphPipes ||
            nested?.pipesList ||
            nested?.pipesListFiltered ||
            nested?.pipeDataGraph ||
            d?.pipesListFiltered ||
            d?.pipeDataGraph ||
            []
        )
      );
      if (cb) cb(body);
    } else if (!ok) {
      notification.error({ message: body?.message || 'Failed to load report data' });
      if (err) err();
    }
    yield put(pipefitterSetReportsLoading(false));
  } catch (error) {
    yield put(pipefitterSetReportsLoading(false));
    const status = error?.response?.status ?? error?.status;
    if (status !== 410) {
      notification.error({ message: error?.message || 'Failed to get all pipe data' });
      if (action.err) action.err();
    }
  }
}
export default function* pipefitterSaga() {
  yield all([
    takeLatest(PIPEFITTER_FETCH_PIPE_LENGTH, fetchPipeLengthSaga),
    takeLatest(PIPEFITTER_FETCH_PROJECT_LIST, fetchProjectListSaga),
    takeLatest(PIPEFITTER_GET_CONFIGURATION, getConfigurationSaga),
    takeLatest(PIPEFITTER_FETCH_PIPE_STATISTICS, fetchPipeStatisticsSaga),
    takeLatest(PIPEFITTER_FETCH_PIPE_STATUS, fetchPipeStatusSaga),
    takeLatest(PIPEFITTER_FETCH_PROJECT_SUMMARY_DATA, fetchProjectSummaryDataSaga),
    takeLatest(PIPEFITTER_UPLOAD_JSON_FILES, uploadJsonFilesSaga),
    takeLatest(PIPEFITTER_CREATE_SPREAD_CONFIGURATION, createSpreadConfigurationSaga),
    takeLatest(PIPEFITTER_UPDATE_SPREAD_CONFIGURATION, updateSpreadConfigurationSaga),
    takeLatest(PIPEFITTER_DELETE_SPREAD_CONFIGURATION, deleteSpreadConfigurationSaga),
    takeLatest(PIPEFITTER_CREATE_SUB_SPREAD_CONFIGURATION, createSubSpreadConfigurationSaga),
    takeLatest(PIPEFITTER_UPDATE_SUB_SPREAD_CONFIGURATION, updateSubSpreadConfigurationSaga),
    takeLatest(PIPEFITTER_DELETE_SUB_SPREAD_CONFIGURATION, deleteSubSpreadConfigurationSaga),
    takeLatest(PIPEFITTER_SORT_SPREAD_CONFIGURATION, sortSpreadConfigurationSaga),
    takeLatest(PIPEFITTER_SORT_SUB_SPREAD_CONFIGURATION, sortSubSpreadConfigurationSaga),
    takeLatest(PIPEFITTER_FETCH_SEQUENCE_CONFIGURATION_LIST, fetchSequenceListSaga),
    takeLatest(PIPEFITTER_GENERATE_SEQUENCE_CONFIGURATION_LIST, generateSequenceSaga),
    takeLatest(PIPEFITTER_PIPE_BASED_GENERATE_SEQUENCE, pipeBasedGenerateSequenceSaga),
    takeLatest(PIPEFITTER_UPDATE_SPREAD_NAME, updateSpreadNameSaga),
    takeLatest(PIPEFITTER_DELETE_SEQUENCE_SPREAD, deleteSequenceSaga),
    takeLatest(PIPEFITTER_FETCH_SEQUENCE_REPORT, fetchSequenceReportSaga),
    takeLatest(PIPEFITTER_APPROVE_SEQUENCE_SPREAD, approveSequenceSaga),
    takeLatest(PIPEFITTER_SEND_EMAIL_SEQUENCE, sendEmailSequenceSaga),
    takeLatest(PIPEFITTER_MIN_MAX_CALCULATION, minMaxCalculationSaga),
    takeLatest(PIPEFITTER_FETCH_PIPE_DATA_GRAPH, fetchPipeDataGraphSaga),
    takeLatest(PIPEFITTER_FETCH_PROJECT_STATUS_PIPE_DATA, fetchProjectStatusPipeDataSaga),
    takeLatest(PIPEFITTER_FETCH_PROJECT_REPORT, fetchProjectReportSaga),
    takeLatest(PIPEFITTER_FETCH_PROJECT_SUMMARY_REPORT, fetchProjectSummaryReportSaga),
    takeLatest(PIPEFITTER_SEND_EMAIL_PIPE_DATA, sendEmailPipeDataSaga),
    takeLatest(PIPEFITTER_SEND_EMAIL_PROJECT_SUMMARY, sendEmailProjectSummarySaga),
    takeLatest(PIPEFITTER_ASSIGN_PIPE_STATUS_DATA, assignPipeStatusDataSaga),
    takeLatest(PIPEFITTER_CREATE_UPDATE_PIPE_DATA, createUpdatePipeDataSaga),
    takeLatest(PIPEFITTER_DELETE_PIPE_DATA, deletePipeDataSaga),
    takeLatest(PIPEFITTER_UPDATE_PIPE_STATUS, updatePipeStatusSaga),
    takeLatest(PIPEFITTER_GENERATE_REPORT_PIPE_DATA, generateReportPipeDataSaga),
    takeLatest(PIPEFITTER_GENERATE_REPORT_PIPE_DATA_PIPEWISE, generateReportPipeDataPipewiseSaga),
    takeLatest(PIPEFITTER_GENERATE_REPORT_PROJECT_SUMMARY, generateReportProjectSummarySaga),
    takeLatest(PIPEFITTER_GET_ALL_PIPE_DATA, getAllPipeDataSaga),
    takeLatest(PIPEFITTER_TRIGGER_REPORT_COMPLIMENTARY, triggerReportComplimentarySaga),
    watchRandomCompatSubmit(),
  ]);
}
